// React
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

// MUI
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

import { Grid } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { SnackbarsCustomStyle, AUTO_HIDE_DURATION, MAX_SNACK } from "@Layouts/components/Snackbars";

// Components
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CommissionSideBar from "@Layouts/components/SideBars/CommissionSideBar";
import OEMDataBody from "./OEMDataBody.tsx";

// Hooks
import { gql, useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { RootState } from "@/redux/store.ts";
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";

const QUERY_IDENTIFIERS = ["oemData", "financialData", "oemGeneralTabs"];

function createSubscriptionObject(subscriptionName: string, selected: ICommissionCycle) {
	return {
		context: subscriptionName,
		query: gql`
			subscription ${subscriptionName}($locationId: String, $commissionCycleId: String) {
				${subscriptionName}(locationId: $locationId, commissionCycleId: $commissionCycleId) {
					commissionCycleId
				}
			}
		`,
		variables: {
			commissionCycleId: selected?._id,
		},
	};
}

export default function OEMDataLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<ICommissionCycle | null>(null);
	const open = useRef(true);

	// Global State
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	// Local State
	const [selection, setSelection] = useState<ICommissionCycle | null>(null);
	const [updateData, setUpdateData] = useState(null);
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	const sideBar = useMemo(() => {
		const sideSelection = function (selected: ICommissionCycle) {
			if (selected?._id !== lastSelected?.current?._id) {
				open.current = !open.current;
				lastSelected.current = selected;
				setSelection(selected);

				addSubscription(
					["oemDataPage", selected?._id],
					[
						// Financial Statements
						createSubscriptionObject("onUpdateMitsubishiFinancialStatement", selected),
						createSubscriptionObject("onUpdateGeneralMotorsFinancialStatement", selected),
						createSubscriptionObject("onUpdateVolvoFinancialStatement", selected),
						createSubscriptionObject("onUpdateStellantisFinancialStatement", selected),
						// National Rankings
						createSubscriptionObject("onUpdateMitsubishiNationalRanking", selected),
						createSubscriptionObject("onUpdateVolvoNationalRanking", selected),
						createSubscriptionObject("onUpdateGeneralMotorsNationalRanking", selected),
						createSubscriptionObject("onUpdateStellantisNationalRanking", selected),
						// Market Share
						createSubscriptionObject("onUpdateGeneralMotorsMarketShare", selected),
						createSubscriptionObject("onUpdateMitsubishiMarketShare", selected),
						createSubscriptionObject("onUpdateVolvoMarketShare", selected),
						createSubscriptionObject("onUpdateStellantisMarketShare", selected),
						// CSI
						createSubscriptionObject("onUpdateGeneralMotorsCSISingleMetric", selected),
						createSubscriptionObject("onUpdateMitsubishiCSISingleMetric", selected),
						createSubscriptionObject("onUpdateVolvoCSISingleMetric", selected),
						createSubscriptionObject("onUpdateStellantisCSISingleMetric", selected),
						// SSI
						createSubscriptionObject("onUpdateMitsubishiSSISingleMetric", selected),
						createSubscriptionObject("onUpdateStellantisSSISingleMetric", selected),
						// EBE
						createSubscriptionObject("onUpdateGeneralMotorsEBE", selected),
						// SFE
						createSubscriptionObject("onUpdateGeneralMotorsSFE", selected),
						// PASE
						createSubscriptionObject("onUpdateGeneralMotorsPASE", selected),
						// Pinnacle
						createSubscriptionObject("onUpdateGeneralMotorsPinnacle", selected),
					]
				);
			}
		};

		return <CommissionSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription]);

	useEffect(() => {
		return () => {
			setSelection(null);
			setUpdateData(null);
			lastSelected.current = null;
			open.current = true;

			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [clearSubscriptionContext, locationId]);

	return (
		<SnackbarProvider
			maxSnack={MAX_SNACK}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			autoHideDuration={AUTO_HIDE_DURATION}
			iconVariant={{ 
				error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
				success: <DoneIcon fontSize="small" className="mr-2" /> 
			}}
			Components={{
				error: SnackbarsCustomStyle,
				success: SnackbarsCustomStyle
			}}
		>
			<BodyLayer floatingContent={<CollapsibleLeftPanel permanentTrigger={open.current} control={sideBar} />}>
				<OEMDataBody selected={selection} />
			</BodyLayer>
			<Grid item xs={12} mb={2} paddingLeft="20px" marginBottom="0px">
				<TextIconMessage icon title={"Last Update"} content={updateData} showContent={Boolean(updateData)} />
			</Grid>
		</SnackbarProvider>
	);
}
